import React, { createRef, useEffect, useState, useContext } from 'react'
import styled from 'styled-components'
import { useSpring, animated } from 'react-spring'
import throttle from 'lodash/throttle'

import Heading from '@components/Heading'
import Section from '@components/Section'
import IntersectionObserver from '@components/IntersectionObserver'
import Sticky, { StickyState } from '@components/Sticky'
import { ContactContext } from '@components/Contact/Contact.Context'

import media from '@styles/media'
import { getWindowDimensions } from '@utils'
import { SITE_NAME } from '@utils/siteConfig'

import HomeServicesMobile from './Home.Services.Mobile'

export const services = [
  {
    heading: 'SwasthaID',
    list: [
      "Nepal's first inter-hospital health data network",
      'Secure, instant sharing of medical records',
      'Patients control their own health data',
    ],
    link: {
      text: 'Learn about SwasthaID',
      to: '/contact',
    },
  },
  {
    heading: 'Revvlow',
    list: [
      'Monetize unsold inventory in real time',
      'Dynamic pricing for cinemas, events & hospitality',
      'Zero disruption to primary pricing strategy',
    ],
    link: {
      text: 'Learn about Revvlow',
      to: '/contact',
    },
  },
  {
    heading: 'OsadAI',
    list: [
      'AI-driven natural medicine & wellness platform',
      'Combines Ayurveda with modern AI formulation',
      'Scientifically-backed nutraceutical solutions',
    ],
    link: {
      text: 'Learn about OsadAI',
      to: '/contact',
    },
  },
]

const appImages = {
  first: '/images/apps/swasthaid-logo-d.png',
  second: '/images/apps/revvlow.jpg',
  third: '/images/apps/osadai.png',
}

const calculateActive = (progress: number) => (index: number): boolean => {
  const total = services.length
  const nextThreshold = ((100 / total) * (index + 1)) / 100
  const threshold = ((100 / total) * index) / 100

  if (index === 0) {
    return progress < nextThreshold
  }

  if (index === 2) {
    return progress > threshold
  }

  return progress > threshold && progress < nextThreshold
}

const calculateOffset = (progress: number) => {
  if (typeof window === 'undefined') return 0

  if (document.getElementById('grid-value')) {
    const gridElement = document.getElementById('grid-value') as HTMLElement
    const { height, top } = gridElement.getBoundingClientRect()

    const total = (height + 45) * 2
    let offset = total * progress

    if (progress < 0) {
      offset = 0
    }

    return {
      height,
      offset,
      top,
    }
  }

  return {}
}

const calcOpacity = (entering: boolean, top: number): { opacity?: number } => {
  const opacity = Math.abs(top) > 250 ? 0 : 1 - Math.abs(top) / 250

  return entering ? {} : { opacity }
}

const calcTransform = (offset: number): string =>
  `translateY(${offset * 180}px)`

function getTime() {
  const now = new Date()
  const hours = now.getHours()
  const days = ['Sun.', 'Mon.', 'Tues.', 'Wed.', 'Thurs.', 'Fri.', 'Sat.']
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sept',
    'Nov',
    'Dec',
  ]

  return {
    month: months[now.getMonth()],
    date: now.getDate(),
    day: days[now.getDay()],
    hours: hours > 12 ? hours - 12 : hours,
    minutes: ('0' + now.getMinutes()).slice(-2),
  }
}

function Time() {
  const [time, setTime] = useState(getTime())

  useEffect(() => {
    const tick = setInterval(() => {
      setTime(getTime())
    }, 1000)

    return () => {
      clearInterval(tick)
    }
  }, [])

  return (
    <TimeContainer>
      <Digits>
        {time.hours}:{time.minutes}
      </Digits>
      <FullDate>
        {time.day},{time.month} {time.date}
      </FullDate>
    </TimeContainer>
  )
}

function Code() {
  return (
    null
  )
}

function HomeServices() {
  const config = { mass: 1, tension: 1000, friction: 100 }

  const [props, set] = useSpring(() => ({
    offset: 1,
    config,
  }))
  const heading = createRef()
  const { showContact, toggleContact } = useContext(ContactContext)
  const { first, second, third } = appImages

  useEffect(() => {
    const handleScroll = throttle(() => {
      if (heading.current) {
        const { height } = getWindowDimensions()
        const offset = heading.current.getBoundingClientRect().top
        set({ offset: 1 - offset / height })
      }
    })

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [showContact])

  const animatedStyles = {
    transform: props.offset.interpolate(calcTransform),
    position: 'relative',
    zIndex: 2,
    pointerEvents: 'none',
  }

  return (
    <>
      <HomeServicesDesktop>
        <Section>
          <IntersectionObserver
            render={({ entering, boundingClientRect }) => {
              return (
                <animated.div style={animatedStyles}>
                  <HeadingBackground
                    style={calcOpacity(entering, boundingClientRect.top)}
                  >
                    <LargeHeading ref={heading}>
                      {SITE_NAME} helps you build your dream scalable product.
                    </LargeHeading>
                  </HeadingBackground>
                </animated.div>
              )
            }}
          />
        </Section>
        <Sticky
          cover
          height="333vh"
          render={({ progress }: StickyState) => {
            const getActive = calculateActive(progress)
            const offset = calculateOffset(progress)

            const firstActive: boolean = getActive(0)
            const secondActive: boolean = getActive(1)
            const thirdActive: boolean = getActive(2)

            const background = firstActive
              ? '#556767'
              : secondActive
                ? '#5f6f82'
                : '#59698a'

            const progressStyles = {
              transform: `translateY(${offset.offset}px)`,
              height: '100%',
              top: 0,
              background,
            }

            return (
              <Grid>
                <ImageSlides>
                  <ImageSlide active={firstActive}>
                    <img src={first} alt="SwasthaID" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    <Time />
                  </ImageSlide>
                  <ImageSlide active={secondActive}>
                    <img src={second} alt="Revvlow" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    <Code />
                  </ImageSlide>
                  <ImageSlide active={thirdActive}>
                    <img src={third} alt="OsadAI" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  </ImageSlide>
                </ImageSlides>
                <Column>
                  <Value id="grid-value" active={firstActive}>
                    <Heading.h2>Get Connected</Heading.h2>
                    <List>
                      <ListItem>Share your Visions</ListItem>
                      <ListItem>Know market demands</ListItem>
                      <ListItem>Know our past success</ListItem>
                    </List>
                    <StyledLink
                      onClick={toggleContact}
                      active={firstActive}
                      tabIndex={firstActive ? 0 : -1}
                    >
                      Let's discuss your dream project
                    </StyledLink>
                    <Progress style={progressStyles} />
                  </Value>
                  <Value active={secondActive}>
                    <Transform active={secondActive || thirdActive}>
                      <Heading.h2>Know our Expertise</Heading.h2>
                      <List>
                        <ListItem>Our Software Solutions</ListItem>
                        <ListItem>Our technology Stacks</ListItem>
                        <ListItem>Request Custom Solutions</ListItem>
                      </List>
                    </Transform>
                    <StyledLink
                      onClick={toggleContact}
                      active={secondActive}
                      tabIndex={secondActive ? 0 : -1}
                    >
                      Let us develop your product
                    </StyledLink>
                  </Value>
                  <Value active={thirdActive}>
                    <Transform active={thirdActive}>
                      <Heading.h2>Hire Us</Heading.h2>
                      <List>
                        <ListItem>Planning Products Release</ListItem>
                        <ListItem>Making Market Plans and Delivery</ListItem>
                        <ListItem>Launch your dream projects</ListItem>
                      </List>
                    </Transform>
                    <StyledLink
                      onClick={toggleContact}
                      active={thirdActive}
                      tabIndex={thirdActive ? 0 : -1}
                    >
                      Discuss workflow and marketing
                    </StyledLink>
                  </Value>
                </Column>
                <Column gradient />
                <Column gradient />
                <Column />
              </Grid>
            )
          }}
        />
      </HomeServicesDesktop>
      <HomeServicesMobile />
    </>
  )
}

export default HomeServices

const HomeServicesDesktop = styled.div`
  background: #101216;
  padding-top: 60px;

  ${media.tablet`
    display: none;
  `}
`

const HeadingBackground = styled.div`
  position: relative;
  -webkit-background-clip: text;
  background-clip: text;
  background-repeat: no-repeat;
  background-image: radial-gradient(ellipse farthest-corner at right bottom, #FEDB37 0%, #FDB931 8%, #9f7928 30%, #8A6E2F 40%, transparent 80%),
  radial-gradient(ellipse farthest-corner at left top, #FFFFFF 0%, #FFFFAC 8%, #D1B464 25%, #5d4a1f 62.5%, #5d4a1f 100%);
  color: transparent !important;
  max-width: 800px;
  padding-bottom: 400px;
  margin-bottom: -250px;
  z-index: 4;
`

const LargeHeading = styled.p`
  display: inline;
  font-weight: 700;
  font-size: 70px;
  letter-spacing: -0.5px;
  line-height: 1.2;
  font-family: ${p => p.theme.fontfamily.serif};
  background: transparent;
  color: transparent;
  padding-bottom: 200px;
`

const Grid = styled(Section)`
  height: 100vh;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
`

const Column = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 50px 0;

  &::before {
    content: '';
    width: 1px;
    height: 100%;
    position: absolute;
    top: 0;
    right: 0;
    background: ${p =>
    p.gradient
      ? `linear-gradient(
            rgba(255, 255, 255, 0.1),
            transparent 33%,
            transparent 70%,
            rgba(255, 255, 255, 0.1)
          )`
      : 'rgba(255, 255, 255, 0.1)'};
  }
`

const Value = styled.div`
  position: relative;

  &:not(:last-child) {
    margin-bottom: 45px;
  }

  h2 {
    color: ${p => (p.active ? '#fff' : p.theme.colors.grey)};
    transition: color 0.3s var(--ease-out-quad);
  }

  li {
    color: ${p => (p.active ? '#fff' : p.theme.colors.grey)};
    transition: color 0.3s var(--ease-out-quad);
  }

  ${media.desktop_medium`
    &:not(:last-child) {
      margin-bottom: 15px;
    }
  `}

  @media screen and (max-height: 800px) {
    &:not(:last-child) {
      margin-bottom: 15px;
    }
  }
`

const Transform = styled.div`
  transform: translateY(${p => (p.active ? 0 : 42)}px);
  transition: transform 0.6s var(--ease-out-cubic);
`

const StyledLink = styled.button`
  font-weight: 600;
  color: ${p => p.theme.colors.gold};
  opacity: ${p => (p.active ? 1 : 0)};
  transition: opacity 0.3s var(--ease-out-quad);
  cursor: pointer;
  pointer-events: ${p => (p.active ? 'initial' : 'none')};

  &:hover,
  &:focus {
    text-decoration: underline;
  }
`

const List = styled.ul`
  list-style: none;
  margin-bottom: 20px;
`

const ListItem = styled.li`
  color: #73737d;
`

const Progress = styled.div`
  position: absolute;
  width: 1px;
  right: 0px;
  z-index: 1;
  background: ${p => p.theme.colors.grey};
  transition: opacity 0.3s var(--ease-out-quad);
`

const ImageSlides = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  right: 80px;
  width: 70%;
  height: 100%;
  pointer-events: none;

  & > div {
    width: 100%;
  }
`

const ImageSlide = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 2%;
  right: 0;
  width: 88%;
  height: 100%;
  pointer-events: none;
  opacity: ${p => (p.active ? 1 : 0)};
  transition: opacity 0.3s var(--ease-out-quad);

  & > div {
    width: 100%;
  }
`

const Digits = styled.div`
  font-size: 24px;
  font-weight: 200;

  @media only screen and (max-width: 1220px) {
    font-size: 18px;
  }
`

const TimeContainer = styled.span`
  position: absolute;
  width: 50px;
  top: 12%;
  left: 17.9%;
  transform: rotate(-6deg);
  color: #fafafa;
  opacity: 0.7;
  text-align: center;

  @media only screen and (max-height: 800px) {
    top: 22%;
  }

  @media only screen and (max-width: 1300px) {
    left: 15.5%;
  }

  @media only screen and (max-width: 1024px) {
    display: none;
  }
`

const FullDate = styled.div`
  position: relative;
  left: 1px;
  top: 1px;
  font-size: 7px;
  font-weight: 600;

  @media only screen and (max-width: 1220px) {
    font-size: 5px;
  }
`

const CodeContainer = styled.span`
  display: block;
  color: #33749f;
  font-size: 6px;

  @media only screen and (max-width: 1100px) {
    font-size: 5px;
  }
`

const VectorTop = styled.div`
  position: absolute;
  left: 33%;
  top: 38%;
  max-width: 18%;

  @media only screen and (max-height: 800px) {
    top: 35%;
  }

  @media only screen and (max-width: 1100px) {
    top: 42%;
    max-width: 22%;
  }

  @media only screen and (max-width: 900px) {
    display: none;
  }
`

const VectorBottom = styled.div`
  position: absolute;
  left: 33%;
  top: 50%;
  max-width: 18%;

  @media only screen and (max-height: 800px) {
    top: 50%;
  }

  @media only screen and (max-width: 1100px) {
    max-width: 22%;
  }

  @media only screen and (max-width: 900px) {
    display: none;
  }
`
